class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.handleDocumentClick = this.handleDocumentClick.bind(this);
    this.handleCloseButtonClick = this.handleCloseButtonClick.bind(this);
    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    
    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (overlay) {
      overlay.addEventListener('click', this.close.bind(this));
    }
    
    this.setHeaderCartIconAccessibility();
    this.setupCloseButton();
    this.setupAutoOpenOnAddToCart();
    
    // Setup cart count updates
    this.setupCartCountUpdates();
    
    // Use MutationObserver to watch for DOM changes
    this.setupMutationObserver();
  }

  setupCartCountUpdates() {
    // Listen for Shopify cart update events
    document.addEventListener('cart:update', () => {
      this.updateHeaderCartCount();
    });
    
    // Listen for AJAX cart updates
    document.addEventListener('ajax:complete', (event) => {
      if (event.detail && event.detail.url && 
          (event.detail.url.includes('/cart/add') || 
           event.detail.url.includes('/cart/update') ||
           event.detail.url.includes('/cart/change'))) {
        setTimeout(() => {
          this.updateHeaderCartCount();
        }, 200);
      }
    });
    
    // Listen for Shopify section reloads
    document.addEventListener('shopify:section:load', () => {
      setTimeout(() => {
        this.updateHeaderCartCount();
      }, 100);
    });
    
    // Initial cart count
    this.updateHeaderCartCount();
  }

  updateHeaderCartCount() {
    // Method 1: Fetch cart data from Shopify AJAX API
    fetch('/cart.js')
      .then(response => response.json())
      .then(cart => {
        const totalItems = cart.item_count;
        this.updateCartCountDisplay(totalItems);
      })
      .catch(error => {
        console.error('Error fetching cart:', error);
        // Fallback to reading from DOM
        this.updateCartCountFromDOM();
      });
  }

  updateCartCountFromDOM() {
    const cartCount = this.getCartItemCount();
    this.updateCartCountDisplay(cartCount);
  }

  updateCartCountDisplay(count) {
    // Update all cart count elements in the header
    const cartCountElements = document.querySelectorAll('.cart-count-bubble span, .cart-count, [data-cart-count]');
    
    cartCountElements.forEach(element => {
      element.textContent = count;
      
      // Update aria labels
      const cartLink = element.closest('#cart-icon-bubble');
      if (cartLink) {
        cartLink.setAttribute('aria-label', `Cart, ${count} items`);
      }
    });
    
    // Also update any hidden cart count inputs
    const hiddenCountInputs = document.querySelectorAll('input[name="cart_count"]');
    hiddenCountInputs.forEach(input => {
      input.value = count;
    });
    
    // Store for later comparison
    this.previousCartCount = count;
  }

  setupMutationObserver() {
    // Watch for changes in the drawer content
    const observer = new MutationObserver(() => {
      if (this.classList.contains('active')) {
        // Re-attach close button listeners when content changes
        this.setupCloseButton();
        // Update cart count when drawer content changes
        this.updateHeaderCartCount();
      }
    });
    
    observer.observe(this, { 
      childList: true, 
      subtree: true 
    });
  }

  setupAutoOpenOnAddToCart() {
    document.addEventListener('ajax:complete', (event) => {
      if (event.detail && event.detail.url && event.detail.url.includes('/cart/add')) {
        setTimeout(() => {
          this.updateHeaderCartCount(); // Update cart count
          if (!this.classList.contains('active')) {
            this.open();
          } else {
            // If already open, re-setup close buttons
            this.setupCloseButton();
          }
        }, 100);
      }
    });

    document.addEventListener('click', (event) => {
      const addToCartButton = event.target.closest('[name="add"], .add-to-cart, .product-form__submit, button[type="submit"][form*="product-form"]');
      if (addToCartButton) {
        this.updateHeaderCartCount(); // Update cart count immediately
        const form = addToCartButton.closest('form');
        if (form && (form.action.includes('/cart/add') || form.id.includes('product-form'))) {
          setTimeout(() => {
            this.updateHeaderCartCount(); // Update again after potential AJAX
            if (!this.classList.contains('active')) {
              this.open();
            } else {
              this.setupCloseButton();
            }
          }, 500);
        }
      }
    });

    document.addEventListener('shopify:section:load', (event) => {
      setTimeout(() => {
        this.updateHeaderCartCount();
        if (this.classList.contains('active')) {
          this.setupCloseButton();
        }
        if (event.detail && event.detail.sectionId) {
          const cartCount = this.getCartItemCount();
          if (cartCount > this.previousCartCount) {
            setTimeout(() => {
              if (!this.classList.contains('active')) {
                this.open();
              } else {
                this.setupCloseButton();
              }
            }, 200);
          }
          this.previousCartCount = cartCount;
        }
      }, 100);
    });

    this.previousCartCount = this.getCartItemCount();
    const cartCountObserver = new MutationObserver(() => {
      const newCartCount = this.getCartItemCount();
      if (newCartCount !== this.previousCartCount) {
        this.updateHeaderCartCount(); // Ensure header is updated
        if (newCartCount > this.previousCartCount) {
          setTimeout(() => {
            if (!this.classList.contains('active')) {
              this.open();
            } else {
              this.setupCloseButton();
            }
          }, 300);
        }
      }
      this.previousCartCount = newCartCount;
    });

    const cartIconBubble = document.querySelector('#cart-icon-bubble');
    if (cartIconBubble) {
      cartCountObserver.observe(cartIconBubble, { 
        childList: true, 
        subtree: true, 
        characterData: true,
        attributes: true 
      });
    }
  }

  handleCloseButtonClick(event) {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    this.close();
    this.updateHeaderCartCount(); // Update when closing
  }

  setupCloseButton() {
    if (this.closeButtonDelegation) {
      this.removeEventListener('click', this.closeButtonDelegation);
    }
    
    this.closeButtonDelegation = (event) => {
      const closeButton = event.target.closest('.drawer-close-btn');
      if (closeButton) {
        event.preventDefault();
        event.stopPropagation();
        this.close();
        this.updateHeaderCartCount();
      }
    };
    
    // Add delegated event listener
    this.addEventListener('click', this.closeButtonDelegation);
  }

  getCartItemCount() {
    const cartCountElement = document.querySelector('.cart-count-bubble span');
    if (cartCountElement) {
      const count = parseInt(cartCountElement.textContent);
      return isNaN(count) ? 0 : count;
    }
    return 0;
  }

  updateCartCount() {
    this.previousCartCount = this.getCartItemCount();
    this.updateHeaderCartCount(); // Also update header
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;
    
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    
    setTimeout(() => {this.classList.add('animate', 'active')});

    this.addEventListener('transitionend', () => {
      const containerToTrapFocusOn = this.classList.contains('is-empty') ? this.querySelector('.drawer__inner-empty') : document.getElementById('CartDrawer');
      const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
      if (typeof trapFocus === 'function') {
        trapFocus(containerToTrapFocusOn, focusElement);
      }
    }, { once: true });

    document.body.classList.add('overflow-hidden');
    giftCart();
    
    this.setupCloseButton();
    this.updateHeaderCartCount(); // Update cart count when opening
  
    setTimeout(() => {
      document.addEventListener('click', this.handleDocumentClick);
    }, 10);
  }

  handleDocumentClick(event) {
    if (!this.classList.contains('active')) return;
    
    if (event.target.closest('#cart-icon-bubble')) return;
    
    if (event.target.closest('.drawer-close-btn')) return;
    const cartDrawer = document.getElementById('CartDrawer');

    if (cartDrawer && event.target === cartDrawer) {
      this.close();
      this.updateHeaderCartCount();
      return;
    }

    const drawerInner = this.querySelector('.drawer__inner');
    if (drawerInner && drawerInner.contains(event.target)) return;
    
    if (event.target.closest('#CartDrawer-Overlay')) {
      this.close();
      this.updateHeaderCartCount();
      return;
    }
    
    if (!this.contains(event.target)) {
      this.close();
      this.updateHeaderCartCount();
    }
  }

  close() {
    if (!this.classList.contains('active')) return;
    
    this.classList.remove('active');
    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(this.activeElement);
    }
    document.body.classList.remove('overflow-hidden');
    giftCart();
    
    document.removeEventListener('click', this.handleDocumentClick);
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if(cartDrawerNote.nextElementSibling && cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    if (typeof onKeyUpEscape === 'function') {
      cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
    }
  }

  renderContents(parsedState) {
  const drawerInner = this.querySelector('.drawer__inner');
  if (drawerInner && drawerInner.classList.contains('is-empty')) {
    drawerInner.classList.remove('is-empty');
  }
  this.productId = parsedState.id;
  this.getSectionsToRender().forEach((section) => {
    const sectionElement = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
    if (!sectionElement) return;

    if (section.id === 'cart-icon-bubble') {

      const parsedHTML = new DOMParser().parseFromString(
        parsedState.sections[section.id],
        'text/html'
      );

      const newCartBubble =
        parsedHTML.querySelector('#cart-icon-bubble');

      if (newCartBubble) {
        sectionElement.replaceWith(newCartBubble);
        this.setHeaderCartIconAccessibility();
      }

    } else {

      const newContent = this.getSectionInnerContent(
        parsedState.sections[section.id],
        section.selector
      );

      sectionElement.replaceChildren(...newContent);
    }
    giftCart();
  });

  setTimeout(() => {
    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (overlay) {
      overlay.addEventListener('click', this.close.bind(this));
    }
    this.open();
    this.updateHeaderCartCount();

  });
}

  getSectionInnerContent(html, selector = '.shopify-section') {
  const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
  const element = parsedHTML.querySelector(selector);

    return element ? [...element.childNodes] : [];
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer'
      },
      {
        id: 'cart-icon-bubble',
        selector: '#cart-icon-bubble'
      }
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

function giftCart() {
  const cartItems = document.querySelectorAll('tr.cart-item');
  cartItems.forEach(cartItem => {        
    const dataId = cartItem.getAttribute('data-id');
    const drawerFooter = document.querySelector(`#Gift-Wrapper[data-id="${dataId}"]`);  
    if(!drawerFooter) return;
    const drawerFooterID = drawerFooter.getAttribute('data-id');
    if(drawerFooterID == dataId) {            
      drawerFooter.classList.add('Gift-Wrapper__hide'); 
    }
  });
}

function openCartDrawer() {
  const drawer = document.querySelector('cart-drawer');
  if (drawer && !drawer.classList.contains('active')) {
    drawer.updateHeaderCartCount(); // Update before opening
    drawer.open();
  }
}

document.addEventListener('click', function(event) {
  const quickAddButton = event.target.closest('.quick-add__button, .product-card__add-button, [data-quick-add]');
  if (quickAddButton) {
    setTimeout(() => {
      const drawer = document.querySelector('cart-drawer');
      if (drawer) {
        drawer.updateHeaderCartCount(); // Update cart count
        if (!drawer.classList.contains('active')) {
          drawer.open();
        } else if (drawer.classList.contains('active')) {
          // Re-attach close button handlers
          drawer.setupCloseButton();
        }
      }
    }, 600);
  }
});

// Also listen for quantity updates and remove buttons in the cart drawer
document.addEventListener('click', function(event) {
  // Listen for quantity update buttons
  const quantityButton = event.target.closest('.quantity__button, .cart-item__quantity button');
  if (quantityButton) {
    setTimeout(() => {
      const drawer = document.querySelector('cart-drawer');
      if (drawer) {
        drawer.updateHeaderCartCount();
      }
    }, 300);
  }
  
  // Listen for remove buttons
  const removeButton = event.target.closest('.cart-item__remove, [data-cart-item-remove]');
  if (removeButton) {
    setTimeout(() => {
      const drawer = document.querySelector('cart-drawer');
      if (drawer) {
        drawer.updateHeaderCartCount();
      }
    }, 300);
  }
});